import React, { useState } from 'react'
import { Box, Button, Typography, MenuItem } from '@mui/material'
import { useTranslation } from 'react-i18next'
import RowStack from 'components/atoms/container/RowStack'
import { IconContainer } from 'components/atoms/Icons'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CommonDialog from 'components/molecules/notificationOverlay/CommonDialog'
import InfoIcon from '@mui/icons-material/Info'
import { formatDate } from 'utils/formatDateTime'
import BoxWithBackground from 'components/atoms/container/BoxWithBackground'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined'
import EastIcon from '@mui/icons-material/East'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import { ReactComponent as TipsAndUpdatesTwoTowned } from 'assets/images/tipsAndUpdatesTwoTowned.svg'
import { ReactComponent as NoteTakerIcon } from 'assets/images/noteTaker.svg'
import { ReactComponent as NoteTakerWarning } from 'assets/images/noteTakerWarning.svg'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
  MONARK_PRIVACY_POLICY_URL,
  MONARK_SUPPORT_EMAIL
} from 'utils/constants'
import OverFlowMenu from 'components/atoms/OverFlowMenu'
import UserMeetingTranscript from './UserMeetingTranscript'
import { FlatIconButton } from 'components/atoms/button/StyledButton'
import NotificationWrapper from 'components/atoms/container/NotificationWrapper'
import colors from 'styles/colors'
import TurndownService from 'turndown'
import { SUMMARY_TABS_ID } from 'utils/constants'
import { track1on1MeetingAttrs } from 'services/amplitudeMeetingTracking'
import { ActionText } from 'components/atoms/Text'
import { MY_MEETING } from 'routes/constants'
import { useNavigate } from 'react-router-dom'
import { ColStack } from 'components/atoms/container/InCardStack'

export const TabContainer = ({ children, sx = {} }) => {
  return (
    <BoxWithBackground
      id={SUMMARY_TABS_ID}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.GoldenYellow50,
        borderRadius: 2,
        padding: 8,
        width: '100%',
        height: '100%',
        position: 'relative',
        overflowY: 'auto',
        ...sx
      }}>
      {children}
    </BoxWithBackground>
  )
}

export const NoteTakerDisclaimer = () => {
  const [noteTakerDialogOpen, setNoteTakerDialogOpen] = useState(false)
  const { t } = useTranslation()
  return (
    <>
      <FlatIconButton
        onClick={() => setNoteTakerDialogOpen(true)}
        icon={<InfoIcon />}
        iconContainerProps={{ size: 24 }}
      />
      <CommonDialog
        open={noteTakerDialogOpen}
        onClose={() => setNoteTakerDialogOpen(false)}
        titleText={t('myTeam.noteTakerInformation')}
        confirmText={t('confidentiality.gotIt')}
        showCloseButton={true}
        bodyWidth={440}>
        <Typography>{t('myTeam.noteTakerCanMakeMistakes.part1')}</Typography>
        <Typography>
          {t('myTeam.noteTakerCanMakeMistakes.part2')}
          <ActionText
            sx={{ textDecoration: 'underline' }}
            target='_blank'
            href={MONARK_PRIVACY_POLICY_URL}>
            {t('authPages.privacyPolicy')}.
          </ActionText>
        </Typography>
      </CommonDialog>
    </>
  )
}

export const SummaryHead = ({ event, isSummary }) => {
  const turndownService = new TurndownService()
  const copySummary = () => {
    const html = document.getElementById(SUMMARY_TABS_ID)
    if (!html) return
    // Custom rule to handle checkboxes
    turndownService.addRule('checkboxes', {
      filter: node =>
        node.nodeName === 'INPUT' && node.getAttribute('type') === 'checkbox',
      replacement: (_, node) => (node.checked ? '[x] ' : '[ ] ')
    })
    const markdown = turndownService.turndown(html)
    navigator.clipboard.writeText(markdown)

    if (isSummary) track1on1MeetingAttrs('Copy Summary', event)
  }
  return (
    <RowStack justifyContent={'space-between'} paddingBottom={6}>
      <Typography variant='h3'>{formatDate(event.startTime)}</Typography>
      <RowStack spacing={4} width={'auto'}>
        <FlatIconButton onClick={copySummary} icon={<ContentCopyIcon />} />
        <NoteTakerDisclaimer />
      </RowStack>
    </RowStack>
  )
}

export const InsightsFloatingBar = ({
  backgroundColor,
  btnAction = () => {}
}) => {
  const { t } = useTranslation()
  return (
    <BoxWithBackground
      sx={{
        backgroundColor: backgroundColor,
        borderRadius: 2,
        padding: 8,
        position: 'sticky',
        bottom: 0,
        marginTop: 'auto'
      }}>
      <RowStack justifyContent={'space-between'}>
        <RowStack spacing={2} flex={1}>
          <IconContainer icon={<TipsAndUpdatesOutlinedIcon />} />
          <Typography variant='buttonLarge'>
            {t('myTeam.dontMissInsights')}
          </Typography>
        </RowStack>
        <Button
          size='small'
          color={'primary'}
          endIcon={<EastIcon />}
          onClick={btnAction}>
          {t('myTeam.viewInsights')}
        </Button>
      </RowStack>
    </BoxWithBackground>
  )
}

export const InsightsNotification = () => {
  const { t } = useTranslation()
  return (
    <NotificationWrapper
      icon={<TipsAndUpdatesTwoTowned />}
      title={t('myTeam.excitingThingsComing')}
      description={t('myTeam.soonAccessInsights')}>
      <Button
        size='small'
        color={'primary'}
        endIcon={<OpenInNewIcon />}
        href={`mailto:${MONARK_SUPPORT_EMAIL}`}>
        {t('myTeam.shareYourIdeas')}
      </Button>
    </NotificationWrapper>
  )
}

export const ErrorNotification = ({ title, description }) => {
  return (
    <NotificationWrapper
      icon={<NoteTakerWarning />}
      title={title}
      description={description}
    />
  )
}

export const PrevSummaryNotification = ({
  isPrevSummary = false,
  hasTranscript
}) => {
  const { t } = useTranslation()
  return (
    <>
      {!isPrevSummary ? (
        <NotificationWrapper
          icon={hasTranscript ? <NoteTakerWarning /> : <NoteTakerIcon />}
          title={
            hasTranscript
              ? t('myTeam.noPreviousMeeting')
              : t('myTeam.previousMeetingSummary')
          }
          description={
            hasTranscript
              ? t('myTeam.wrongWithLastMeeting')
              : t('myTeam.pastMeetingNotes')
          }
        />
      ) : (
        <Box
          sx={{
            padding: 6,
            borderRadius: 2,
            border: `1px solid ${colors.Grey200}`
          }}>
          <ColStack spacing={8}>
            <Typography variant='body1KindaSmall' fontWeight={500}>
              {t('myTeam.NoMeetingSummary')}
            </Typography>

            <Typography variant='body1KindaSmall'>
              {t('myTeam.makeSureBotAllowed')}
            </Typography>
          </ColStack>
        </Box>
      )}
    </>
  )
}

export const TranscriptMenu = ({ event, connection }) => {
  const { t } = useTranslation()
  const [openTranscript, setOpenTranscript] = useState(false)
  const handleViewTranscript = () => {
    track1on1MeetingAttrs('Click transcript', event)
    setOpenTranscript(true)
  }
  return (
    <>
      <OverFlowMenu icon={<MoreVertIcon />}>
        <MenuItem onClick={handleViewTranscript}>
          <Typography variant='body1Small'>
            {t('myTeam.viewCompleteTranscript')}
          </Typography>
        </MenuItem>
      </OverFlowMenu>

      <UserMeetingTranscript
        event={event}
        connection={connection}
        open={openTranscript}
        setOpen={setOpenTranscript}
      />
    </>
  )
}

export const OpenEventInNewTab = ({ event }) => {
  const navigate = useNavigate()
  return (
    <FlatIconButton
      icon={<OpenInNewRoundedIcon />}
      iconContainerProps={{ size: 16 }}
      onClick={() => {
        navigate(MY_MEETING, {
          state: {
            userCalendarEventId: event?.id
          }
        })
      }}
    />
  )
}
